import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';
import { supabase } from '../utils/supabaseClient';

const SUPABASE_FUNCTION_URL = process.env.REACT_APP_SUPABASE_URL
  ? `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-application`
  : null;

const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

const ALLOWED_TYPES = [
  'application/pdf',
];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

const INITIAL = { name: '', email: '', phone: '' };

function isIndiaRegion(sourceRegion) {
  return String(sourceRegion || '').trim().toLowerCase() === 'india';
}

function getPhonePlaceholder(sourceRegion, explicitPlaceholder) {
  if (explicitPlaceholder) return explicitPlaceholder;
  return isIndiaRegion(sourceRegion) ? '+91 98765 43210' : '+1 415 555 2671';
}

function getNamePlaceholder(sourceRegion) {
  return isIndiaRegion(sourceRegion) ? 'Aarav Sharma' : 'Jane Smith';
}

function getEmailPlaceholder(sourceRegion) {
  return isIndiaRegion(sourceRegion) ? 'aarav@example.in' : 'jane@example.com';
}

function isValidEmail(value) {
  const email = String(value || '').trim().toLowerCase();
  if (!email || email.includes(' ')) return false;

  const parts = email.split('@');
  if (parts.length !== 2) return false;

  const [local, domain] = parts;
  if (!local || !domain) return false;

  const labels = domain.split('.');
  if (labels.length < 2) return false;

  const labelOk = labels.every((label) => /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(label));
  if (!labelOk) return false;

  const tld = labels[labels.length - 1];
  if (!/^[a-z]{2,}$/.test(tld)) return false;

  return true;
}

function isValidPhoneByRegion(value, sourceRegion) {
  const normalized = String(value || '').replace(/[\s\-().]/g, '');

  if (isIndiaRegion(sourceRegion)) {
    const match = normalized.match(/^\+91([6-9]\d{9})$/);
    return Boolean(match);
  }

  const match = normalized.match(/^\+1(\d{10})$/);
  return Boolean(match);
}

function isPdfFile(file) {
  if (!file) return false;
  const lowerName = String(file.name || '').toLowerCase();
  return ALLOWED_TYPES.includes(file.type) || lowerName.endsWith('.pdf');
}

export default function ApplyModal({ job, onClose, phonePlaceholder, sourceRegion = 'USA' }) {
  const [form, setForm] = useState(INITIAL);
  const [hpToken, setHpToken] = useState('');
  const [resumeFile, setResumeFile] = useState(null);
  const [captchaToken, setCaptchaToken] = useState('');
  const [turnstileError, setTurnstileError] = useState('');
  const [turnstileRenderKey, setTurnstileRenderKey] = useState(0);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpStatus, setOtpStatus] = useState('');
  const overlayRef = useRef(null);
  const firstInputRef = useRef(null);

  const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
  const effectivePhonePlaceholder = getPhonePlaceholder(sourceRegion, phonePlaceholder);
  const namePlaceholder = getNamePlaceholder(sourceRegion);
  const emailPlaceholder = getEmailPlaceholder(sourceRegion);

  const hasDraft = Boolean(
    form.name.trim()
    || form.email.trim()
    || form.phone.trim()
    || resumeFile
    || otpCode.trim()
    || otpSent
    || captchaToken,
  );

  const requestClose = useCallback(() => {
    if (status === 'submitting') return;
    if (status !== 'success' && hasDraft) {
      const ok = window.confirm('Discard your in-progress application?');
      if (!ok) return;
    }
    onClose();
  }, [status, hasDraft, onClose]);

  // Focus first input when modal opens
  useEffect(() => {
    firstInputRef.current?.focus();
  }, []);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        requestClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [requestClose]);

  // Prevent background scroll
  useEffect(() => {
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousOverflow = document.body.style.overflow;
    document.documentElement.classList.add('modal-open');
    document.body.classList.add('modal-open');
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.classList.remove('modal-open');
      document.body.classList.remove('modal-open');
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  function validate() {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Full name is required.';
    if (!form.email.trim()) {
      errs.email = 'Email is required.';
    } else if (!isValidEmail(form.email)) {
      errs.email = 'Enter a valid email address with a valid domain.';
    }
    if (!form.phone.trim()) {
      errs.phone = 'Phone number is required.';
    } else if (!isValidPhoneByRegion(form.phone, sourceRegion)) {
      errs.phone = isIndiaRegion(sourceRegion)
        ? 'Use India format with country code, e.g. +91 98765 43210.'
        : 'Use US format with country code, e.g. +1 415 555 2671.';
    }
    if (!resumeFile) {
      errs.resume = 'Please attach your resume.';
    } else if (!isPdfFile(resumeFile)) {
      errs.resume = 'Only PDF files are accepted.';
    } else if (resumeFile.size > MAX_FILE_SIZE) {
      errs.resume = 'File must be under 5 MB.';
    }
    if (!turnstileSiteKey) {
      errs.captcha = 'CAPTCHA is not configured. Please contact support.';
    } else if (!otpSent && !captchaToken) {
      errs.captcha = 'Please complete CAPTCHA verification.';
    }
    if (otpSent && !otpCode.trim()) {
      errs.otp = 'Enter the OTP sent to your email.';
    }
    return errs;
  }

  function handleChange(e) {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    setErrors(errs => ({ ...errs, [e.target.name]: undefined }));
  }

  function handleFileChange(e) {
    const file = e.target.files[0] || null;
    setResumeFile(file);
    setErrors(errs => ({ ...errs, resume: undefined }));
  }

  async function requestOtp(trimmedEmail) {
    if (!supabase) {
      throw new Error('OTP verification service is not configured. Please contact support.');
    }

    const { error } = await supabase.auth.signInWithOtp({
      email: trimmedEmail,
      options: {
        shouldCreateUser: true,
        captchaToken,
      },
    });
    if (error) {
      throw error;
    }

    setOtpSent(true);
    setOtpCode('');
    setCaptchaToken('');
    setTurnstileRenderKey((prev) => prev + 1);
    setOtpStatus('Verification code sent. Enter the OTP from your email.');
  }

  async function verifyOtp(trimmedEmail, otp) {
    if (!supabase) {
      throw new Error('OTP verification service is not configured. Please contact support.');
    }

    const { error: verifyError } = await supabase.auth.verifyOtp({
      email: trimmedEmail,
      token: otp,
      type: 'email',
    });
    if (verifyError) {
      throw verifyError;
    }

    const { data } = await supabase.auth.getSession();
    const accessToken = data?.session?.access_token ?? '';
    if (!accessToken) {
      throw new Error('Could not establish authenticated session. Please retry.');
    }

    setOtpStatus('Email verified. Submitting your application...');

    return accessToken;
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (hpToken.trim()) {
      setStatus('error');
      setErrorMsg('Submission blocked.');
      return;
    }

    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setStatus('submitting');
    setErrorMsg('');
    setOtpStatus('');

    try {
      if (!SUPABASE_FUNCTION_URL) {
        throw new Error('Careers service is not configured. Please contact support.');
      }
      if (!supabase) {
        throw new Error('Supabase authentication is not configured. Please contact support.');
      }

      const trimmedEmail = form.email.trim().toLowerCase();

      if (!captchaToken) {
        setErrors((prev) => ({ ...prev, captcha: 'Please complete the CAPTCHA first.' }));
        setStatus('idle');
        return;
      }

      if (!otpSent) {
        await requestOtp(trimmedEmail);
        setStatus('idle');
        return;
      }

      if (!otpCode.trim()) {
        setErrors((prev) => ({ ...prev, otp: 'Enter the OTP sent to your email.' }));
        setStatus('idle');
        return;
      }

      const accessToken = await verifyOtp(trimmedEmail, otpCode.trim());

      const fd = new FormData();
      fd.append('name', form.name.trim());
      fd.append('email', trimmedEmail);
      fd.append('phone', form.phone.trim());
      fd.append('jobId', job.id);
      fd.append('jobTitle', job.title);
      fd.append('jobLocation', job.location);
      fd.append('sourceRegion', sourceRegion);
      fd.append('companyWebsite', hpToken);
      fd.append('turnstileToken', captchaToken);
      fd.append('file', resumeFile);

      const res = await fetch(SUPABASE_FUNCTION_URL, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          ...(SUPABASE_ANON_KEY
            ? {
              apikey: SUPABASE_ANON_KEY,
            }
            : {}),
        },
        body: fd,
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        setCaptchaToken('');
        setTurnstileRenderKey((prev) => prev + 1);
        if (res.status === 409) {
          throw new Error('This email has already submitted an application.');
        }
        if (res.status === 429) {
          throw new Error('Please wait before submitting again.');
        }
        throw new Error(json.error || `Server error (${res.status})`);
      }

      await supabase.auth.signOut();

      setStatus('success');
    } catch (err) {
      setStatus('error');
      const raw = String(err?.message || '').toLowerCase();
      const isConnectivityError = raw.includes('failed to fetch')
        || raw.includes('networkerror')
        || raw.includes('load failed')
        || raw.includes('connection refused')
        || raw.includes('timeout');
      if (isConnectivityError) {
        setErrorMsg('Unable to reach the careers service right now. Please try again in a moment.');
      } else {
        setErrorMsg(err.message || 'Something went wrong. Please try again.');
      }
    }
  }

  return (
    <div className="apply-modal-overlay" ref={overlayRef} role="dialog" aria-modal="true" aria-labelledby="apply-modal-title">
      <div className="apply-modal">
        <button className="apply-modal-close" onClick={requestClose} aria-label="Close">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>

        {status === 'success' ? (
          <div className="apply-modal-success">
            <div className="apply-modal-success-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#12683e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="9 12 11 14 15 10"/>
              </svg>
            </div>
            <h3>Application Submitted!</h3>
            <p>Thank you, <strong>{form.name}</strong>. Your application for <strong>{job.title}</strong> has been received. Our HR team will be in touch soon.</p>
            <button className="apply-modal-btn" onClick={onClose}>Close</button>
          </div>
        ) : (
          <>
            <h2 id="apply-modal-title" className="apply-modal-title">
              {job.title}
              <span className="apply-modal-id">ID: {job.id}</span>
            </h2>
            <p className="apply-modal-meta">{job.company} &bull; {job.location}</p>

            <form onSubmit={handleSubmit} noValidate className="apply-modal-form">
              <div className="apply-field">
                <label htmlFor="am-name">Full Name <span aria-hidden="true">*</span></label>
                <input
                  id="am-name"
                  ref={firstInputRef}
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={handleChange}
                  placeholder={namePlaceholder}
                  autoComplete="name"
                  aria-invalid={!!errors.name}
                />
                {errors.name && <p className="apply-field-error">{errors.name}</p>}
              </div>

              <div className="apply-field">
                <label htmlFor="am-email">Email <span aria-hidden="true">*</span></label>
                <input
                  id="am-email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder={emailPlaceholder}
                  autoComplete="email"
                  aria-invalid={!!errors.email}
                />
                {errors.email && <p className="apply-field-error">{errors.email}</p>}
              </div>

              <div className="apply-field">
                <label htmlFor="am-phone">Phone <span aria-hidden="true">*</span></label>
                <input
                  id="am-phone"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder={effectivePhonePlaceholder}
                  autoComplete="tel"
                  aria-invalid={!!errors.phone}
                />
                {errors.phone && <p className="apply-field-error">{errors.phone}</p>}
              </div>

              {otpSent && (
                <div className="apply-field">
                  <label htmlFor="am-otp">Email OTP <span aria-hidden="true">*</span></label>
                  <input
                    id="am-otp"
                    name="otp"
                    type="text"
                    inputMode="numeric"
                    maxLength={8}
                    value={otpCode}
                    onChange={(e) => {
                      setOtpCode(e.target.value);
                      setErrors((prev) => ({ ...prev, otp: undefined }));
                    }}
                    placeholder="Enter verification code"
                    aria-invalid={!!errors.otp}
                  />
                  {errors.otp && <p className="apply-field-error">{errors.otp}</p>}
                </div>
              )}

              <div className="apply-field">
                <label htmlFor="am-resume">Resume <span aria-hidden="true">*</span> <span className="apply-field-hint">(PDF only, max 5 MB)</span></label>
                <input
                  id="am-resume"
                  name="resume"
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleFileChange}
                  aria-invalid={!!errors.resume}
                />
                {errors.resume && <p className="apply-field-error">{errors.resume}</p>}
              </div>

              <input
                type="text"
                name="companyWebsite"
                tabIndex={-1}
                autoComplete="off"
                value={hpToken}
                onChange={(e) => setHpToken(e.target.value)}
                className="apply-hp-field"
                aria-hidden="true"
              />

              <div className="apply-captcha-wrap">
                {turnstileSiteKey ? (
                  <Turnstile
                    key={turnstileRenderKey}
                    siteKey={turnstileSiteKey}
                    options={{ action: 'careers_apply', cData: 'careers-v1', appearance: 'always' }}
                    onSuccess={(token) => {
                      setCaptchaToken(token || '');
                      setTurnstileError('');
                      setErrors((prev) => ({ ...prev, captcha: undefined }));
                    }}
                    onExpire={() => {
                      setCaptchaToken('');
                      setTurnstileError('CAPTCHA expired. Please verify again.');
                      setTurnstileRenderKey((prev) => prev + 1);
                    }}
                    onError={() => {
                      setCaptchaToken('');
                      setTurnstileError('CAPTCHA failed to load. Please refresh and try again.');
                    }}
                  />
                ) : (
                  <p className="apply-submit-error">CAPTCHA is not configured. Set REACT_APP_TURNSTILE_SITE_KEY.</p>
                )}
                {(errors.captcha || turnstileError) ? (
                  <p className="apply-submit-error" role="alert">{errors.captcha || turnstileError}</p>
                ) : null}
              </div>

              {status === 'error' && (
                <p className="apply-submit-error">{errorMsg}</p>
              )}

              {otpStatus && status !== 'error' && (
                <p className="apply-submit-info">{otpStatus}</p>
              )}

              <button
                type="submit"
                className="apply-modal-btn apply-modal-btn--submit"
                disabled={status === 'submitting'}
              >
                {status === 'submitting'
                  ? 'Processing…'
                  : (!otpSent
                    ? 'Send Verification Code'
                    : 'Verify OTP & Submit')}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
